// inbox-105 — клиенты для settings-зоны.
// Сгруппированы в один файл, чтобы не плодить 6 микро-модулей.

import { ApiError } from './links';

// Re-export для view-уровневых импортов (SettingsXView пользуется одним
// неймспейсом @/api/settings вместо двух).
export { ApiError } from './links';

// ─── Team ─────────────────────────────────────────────
export interface TeamMember {
  id: number;
  email: string;
  name: string;
  role: 'owner' | 'admin' | 'manager' | 'viewer';
  last_active_at: string | null;
  email_confirmed: boolean;
  created_at: string;
}

export interface Invitation {
  id: number;
  email: string;
  role: 'admin' | 'manager' | 'viewer';
  expires_at: string;
  created_at: string;
}

// ─── API keys ─────────────────────────────────────────
export interface ApiKey {
  id: number;
  name: string;
  scopes: string[];
  /** Только последние 4 символа (например `…b5c7`) — server маскирует */
  preview: string;
  created_at: string;
  expires_at: string | null;
  last_used_at: string | null;
  revoked_at: string | null;
}

export interface ApiKeyCreateOut extends ApiKey {
  /** Plaintext — показывается клиенту ОДИН раз сразу после create */
  secret_plain: string;
}

// ─── Webhooks ─────────────────────────────────────────
export interface Webhook {
  id: number;
  url: string;
  events: string[];
  status: 'active' | 'paused' | 'failing';
  delivery_stats?: { sent: number; ok: number; latency_ms: number };
  created_at: string;
}

// ─── Audit ────────────────────────────────────────────
export interface AuditEvent {
  id: number;
  ts: string;
  actor_email: string;
  actor_name: string;
  action: string;
  target_type: string;
  target_id: string | null;
  ip: string | null;
  ua: string | null;
}

// ─── 2FA ──────────────────────────────────────────────
export interface Enable2faOut {
  secret: string;
  provisioning_uri: string;
  issuer: string;
}

export interface Verify2faOut {
  enabled: boolean;
  backup_codes: string[];
}

// ─── Generic fetch wrapper ────────────────────────────
async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(init?.headers || {}) },
    ...init,
  });
  if (!res.ok) {
    let detail = '';
    const cloned = res.clone();
    try {
      const j = await res.json();
      detail = j.detail || j.error || JSON.stringify(j);
    } catch {
      detail = await cloned.text();
    }
    throw new ApiError(res.status, detail);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

// ─── Team API ─────────────────────────────────────────
// inbox-348 — миграция на /api/v1/teams/* префикс (backend inbox-207
// feat/inbox-207-teams-endpoints, ждёт merge в main). До merge endpoint'ы
// 404. После — full team management через единый namespace вместо
// смешанного /users + /invitations.
export const TeamApi = {
  list(): Promise<TeamMember[]> {
    return req<TeamMember[]>('/api/v1/teams/members');
  },
  invitations(): Promise<Invitation[]> {
    return req<Invitation[]>('/api/v1/invitations?status=pending');
  },
  invite(emails: string[], role: 'admin' | 'manager' | 'viewer'): Promise<Invitation[]> {
    return req<Invitation[]>('/api/v1/teams/invite', {
      method: 'POST',
      body: JSON.stringify({ emails, role }),
    });
  },
  patchRole(userId: number, role: TeamMember['role']): Promise<TeamMember> {
    return req<TeamMember>(`/api/v1/teams/members/${userId}/role`, {
      method: 'PATCH',
      body: JSON.stringify({ role }),
    });
  },
  remove(userId: number): Promise<void> {
    return req<void>(`/api/v1/teams/members/${userId}`, { method: 'DELETE' });
  },
};

// ─── API Keys API ─────────────────────────────────────
export const ApiKeysApi = {
  list(): Promise<ApiKey[]> {
    return req<ApiKey[]>('/api/v1/api-keys');
  },
  create(body: { name: string; scopes: string[]; expires_in_days?: number | null }): Promise<ApiKeyCreateOut> {
    return req<ApiKeyCreateOut>('/api/v1/api-keys', { method: 'POST', body: JSON.stringify(body) });
  },
  revoke(id: number): Promise<void> {
    return req<void>(`/api/v1/api-keys/${id}`, { method: 'DELETE' });
  },
};

// ─── Webhooks API ─────────────────────────────────────
export const WebhooksApi = {
  list(): Promise<Webhook[]> {
    return req<Webhook[]>('/api/v1/webhooks');
  },
  create(body: { url: string; events: string[]; secret?: string }): Promise<Webhook> {
    return req<Webhook>('/api/v1/webhooks', { method: 'POST', body: JSON.stringify(body) });
  },
  test(id: number): Promise<{ ok: boolean; status_code: number; latency_ms: number }> {
    return req(`/api/v1/webhooks/${id}/test`, { method: 'POST' });
  },
  delete(id: number): Promise<void> {
    return req<void>(`/api/v1/webhooks/${id}`, { method: 'DELETE' });
  },
};

// ─── Audit API ────────────────────────────────────────
export const AuditApi = {
  list(opts: { limit?: number; offset?: number; action?: string; from?: string; to?: string } = {}): Promise<AuditEvent[]> {
    const q = new URLSearchParams();
    if (opts.limit !== undefined) q.set('limit', String(opts.limit));
    if (opts.offset !== undefined) q.set('offset', String(opts.offset));
    if (opts.action) q.set('action', opts.action);
    if (opts.from) q.set('from', opts.from);
    if (opts.to) q.set('to', opts.to);
    const qs = q.toString();
    return req<AuditEvent[]>(`/api/v1/audit${qs ? '?' + qs : ''}`);
  },
};

// ─── 2FA API ──────────────────────────────────────────
export const TwoFactorApi = {
  enable(): Promise<Enable2faOut> {
    return req<Enable2faOut>('/me/2fa/enable', { method: 'POST' });
  },
  verify(code: string): Promise<Verify2faOut> {
    return req<Verify2faOut>('/me/2fa/verify', { method: 'POST', body: JSON.stringify({ code }) });
  },
  disable(password: string): Promise<void> {
    return req<void>('/me/2fa/disable', { method: 'POST', body: JSON.stringify({ password }) });
  },
};

// ─── Profile (password change) ────────────────────────
export const ProfileApi = {
  changePassword(current: string, new_: string): Promise<void> {
    return req<void>('/me/password', { method: 'PATCH', body: JSON.stringify({ current, new: new_ }) });
  },
};

// ─── Organization ─────────────────────────────────────
export interface Organization {
  id: number;
  slug: string;
  name: string;
  plan: 'free' | 'starter' | 'business' | 'team' | 'enterprise';
  legal_name?: string | null;
  inn?: string | null;
  kpp?: string | null;
}

export const OrgApi = {
  current(): Promise<Organization> {
    return req<Organization>('/api/v1/orgs/current');
  },
  patch(body: Partial<Pick<Organization, 'name' | 'legal_name' | 'inn' | 'kpp'>>): Promise<Organization> {
    return req<Organization>('/api/v1/orgs/current', { method: 'PATCH', body: JSON.stringify(body) });
  },
};
